import React, { useEffect, useState, useMemo } from 'react';
import { Button, Input } from 'src/components';
import { Loader } from 'src/components';
import { useAlerts } from 'src/common/AlertManager';
import { useUserAiBudgetQuery } from 'src/api/queries/aiSettingsQueries';
import { useUpdateUserAiBudgetMutation } from 'src/api/mutations/aiSettingsMutations';
import { AiUserBudgetUpdatePayload } from 'src/types/TAiSettings';

import styles from '../styles.module.scss';

interface AiBudgetTabProps {
  userId: number;
}

const AiBudgetTab: React.FC<AiBudgetTabProps> = ({ userId }) => {
  const { showSuccess, showAlert } = useAlerts();
  const { data, isLoading, error } = useUserAiBudgetQuery(userId);
  const updateMutation = useUpdateUserAiBudgetMutation(userId);

  const [dailyCreditLimit, setDailyCreditLimit] = useState('0');
  const [weeklyCreditLimit, setWeeklyCreditLimit] = useState('0');
  const [monthlyCreditLimit, setMonthlyCreditLimit] = useState('0');

  useEffect(() => {
    if (data) {
      setDailyCreditLimit(String(data.daily_credit_limit ?? 0));
      setWeeklyCreditLimit(String(data.weekly_credit_limit ?? 0));
      setMonthlyCreditLimit(String(data.monthly_credit_limit ?? 0));
    }
  }, [data]);

  const hasChanges = useMemo(() => {
    if (!data) return false;
    return (
      String(data.daily_credit_limit ?? 0) !== dailyCreditLimit ||
      String(data.weekly_credit_limit ?? 0) !== weeklyCreditLimit ||
      String(data.monthly_credit_limit ?? 0) !== monthlyCreditLimit
    );
  }, [data, dailyCreditLimit, weeklyCreditLimit, monthlyCreditLimit]);

  const handleSave = async () => {
    const payload: AiUserBudgetUpdatePayload = {
      daily_credit_limit: parseInt(dailyCreditLimit, 10) || 0,
      weekly_credit_limit: parseInt(weeklyCreditLimit, 10) || 0,
      monthly_credit_limit: parseInt(monthlyCreditLimit, 10) || 0,
    };

    try {
      await updateMutation.mutateAsync(payload);
      showSuccess('AI budget updated');
    } catch {
      showAlert('Failed to update AI budget');
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorBanner}>
        Failed to load AI budget settings. The AI Agent addon may not be enabled for this user.
      </div>
    );
  }

  return (
    <div>
      <h4 className={styles.settingsSectionTitle}>Credit Budget</h4>
      <p className={styles.sectionDescription}>
        Set per-user credit limits. Set to 0 for no per-user credit cap.
      </p>

      <div className={styles.formCard}>
        <div className={styles.formCardGrid}>
          <div className={styles.formCardField}>
            <label>Daily Credit Limit</label>
            <Input
              type="number"
              variant="secondary"
              value={dailyCreditLimit}
              min={0}
              placeholder="0"
              onChange={setDailyCreditLimit}
            />
          </div>
          <div className={styles.formCardField}>
            <label>Weekly Credit Limit</label>
            <Input
              type="number"
              variant="secondary"
              value={weeklyCreditLimit}
              min={0}
              placeholder="0"
              onChange={setWeeklyCreditLimit}
            />
          </div>
          <div className={styles.formCardField}>
            <label>Monthly Credit Limit</label>
            <Input
              type="number"
              variant="secondary"
              value={monthlyCreditLimit}
              min={0}
              placeholder="0"
              onChange={setMonthlyCreditLimit}
            />
          </div>
        </div>
      </div>

      <div className={styles.settingsActions}>
        <Button
          mode="primary"
          size="small"
          onClick={handleSave}
          disabled={!hasChanges || updateMutation.isLoading}
        >
          {updateMutation.isLoading ? 'Saving...' : 'Save AI Budget'}
        </Button>
      </div>
    </div>
  );
};

export default AiBudgetTab;
